<template>
  <div>
    <div>
      <div class="h-panel">
        <b-button
            v-if="mayManageTimetable"
            :to="{name: 'add_river_rumble_timetable_record'}"
            variant="primary">
          Schedule a river rumble
        </b-button>

        <b-button
            :disabled="isDeleteRiverRumblesButtonDisabled"
            :variant="hasSelectedRiverRumbles ? 'danger' : 'light'"
            @click="deleteSelectedRiverRumblesWithConfirmation"
        >
          Delete
        </b-button>
      </div>

      <h2>River rumbles</h2>

      <p v-if="hasScheduleConflict" class="text-danger mb-3">
        {{ scheduleConflictMessage }}
      </p>

      <table class="mb-2">
        <tr>
          <th class="pr-1">
            <label for="window-past-duration-selector" class="mb-0">Past</label>
          </th>
          <td class="pr-4">
            <b-form-select
                id="window-past-duration-selector"
                v-model="windowPastDuration"
                :options="windowDurationOptions"
            />
          </td>
          <th class="pr-1">
            <label for="window-future-duration-selector" class="mb-0">Future</label>
          </th>
          <td>
            <b-form-select
                id="window-future-duration-selector"
                v-model="windowFutureDuration"
                :options="windowDurationOptions"
            />
          </td>
        </tr>
      </table>

      <p v-if="message !== null" class="mt-3">
        {{ message }}
      </p>

      <table v-if="hasRiverRumbleConts" class="table table-bordered text-nowrap">
        <thead class="thead-dark">
        <tr>
          <th>
            <b-form-checkbox :checked="allSelected" @change="allSelected = $event">Id</b-form-checkbox>
          </th>
<!--          <th>Proto</th>-->
          <th>Audience</th>
          <th>Status</th>
          <th>Begin</th>
          <th>End</th>
          <th>Duration</th>
          <th>Seed</th>
          <th>Start limit</th> <!-- Start available until -->
          <th>Min<br>player<br>level</th>
          <th>Min<br>client<br>version</th>
          <th>Actions</th>
        </tr>
        </thead>
        <tr
            v-for="riverRumbleCont in riverRumbleConts"
            :key="riverRumbleCont.riverRumble.id"
            :class="getRiverRumbleClass(riverRumbleCont.riverRumble)"
        >
          <td>
            <b-form-checkbox v-model="riverRumbleCont.selected">
              {{ riverRumbleCont.riverRumble.id }}
            </b-form-checkbox>
          </td>
<!--          <td>-->
<!--            <span-->
<!--                v-b-popover.hover.top="getRiverRumbleProtoName(riverRumbleCont.riverRumble.proto_id)"-->
<!--            >-->
<!--              {{ riverRumbleCont.riverRumble.proto_id | shortRiverRumbleName }}-->
<!--            </span>-->
<!--          </td>-->
          <td>
            {{ riverRumbleCont.riverRumble.player_group_id | getPlayerGroupName }}
          </td>
          <td :class="statusClass(riverRumbleCont.riverRumble)">
            {{ riverRumbleCont.riverRumble | statusName }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble.stime | getMomentMinStr }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble | endAtStr }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble.duration | getDurationStr }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble.seed }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble | availableUntilStr }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble.min_level }}
          </td>
          <td>
            {{ riverRumbleCont.riverRumble.min_client_version | convertVersionCodeToStr }}
          </td>
          <td class="action-box">
            <b-button
                v-if="isEditButtonVisible(riverRumbleCont.riverRumble)"
                :to="{name: 'edit_river_rumble_timetable_record', params: {recordId: riverRumbleCont.riverRumble.id}}"
                size="sm"
                variant="success"
            >
              Edit
            </b-button>
            <b-button
                v-if="isDeleteButtonVisible"
                @click="deleteRecordWithConfirmation(riverRumbleCont.riverRumble)"
                variant="danger"
                size="sm"
            >
              Delete
            </b-button>
          </td>
        </tr>
      </table>

      <loading-indicator v-if="isLoading"/>

    </div>

  </div>
</template>

<script>
import auth from '../../auth.js';
import {HTTP} from '../../http-common.js';
import notifier from '../../notifier.js';
import {confirmAction} from '../../service/confirm.js';

import {isRiverRumbleEditable, RiverRumble} from '../../model/river_rumble.js';

import {getBegin, getEnd, getWindowDurationOptions, TIME_UNIT_MONTH} from '../../model/event_time_filter.js';

import {getPlayerGroupName} from "../../model/event.js";

import {
  convertMomentToStamp,
  convertVersionCodeToStr,
  getMomentMinStr,
  getProtoAlias,
  isRoleAdmin
} from '../../util.js';

import LoadingIndicator from '../LoadingIndicator.vue';

const LOAD_STATE_INIT = 1;
const LOAD_STATE_LOADING = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR = 4;

export default {
  name: 'RiverRumbleTimetable',

  components: {
    LoadingIndicator,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      intersectingIds: null,

      // river rumble containers
      riverRumbleConts: null,

      windowDurationOptions: getWindowDurationOptions(),
      windowPastDuration: DURATION_1_MONTH,
      windowFutureDuration: DURATION_1_MONTH,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {boolean} */
    hasScheduleConflict() {
      const intersectingIds = this.intersectingIds;
      if (intersectingIds === null) {
        return false;
      }
      return intersectingIds.length > 0;
    },

    /** @returns {?string} */
    message() {
      if (this.isLoadedNormal && !this.hasRiverRumbleConts) {
        return 'No river rumbles.';
      }

      return null;
    },

    /** @returns {boolean} */
    hasRiverRumbleConts() {
      const conts = this.riverRumbleConts;
      if (conts === null) {
        return false;
      }

      return conts.length > 0;
    },

    /** @returns {boolean} */
    hasSelectedRiverRumbles() {
      const conts = this.riverRumbleConts;
      if (conts === null) {
        return null;
      }

      for (const cont of conts) {
        if (cont.selected) {
          return true;
        }
      }
      return false;
    },

    selectedRiverRumbleIds() {
      const conts = this.riverRumbleConts;
      if (conts === null) {
        return null;
      }

      if (conts.length === 0) {
        return null;
      }

      const ids = [];

      for (const cont of conts) {
        if (cont.selected) {
          ids.push(cont.riverRumble.id);
        }
      }

      return ids;
    },

    allSelected: {
      get() {
        for (const cont of this.riverRumbleConts) {
          if (!cont.selected) {
            return false;
          }
        }
        return true;
      },

      set(newValue) {
        if (newValue) {
          this.selectAll();
        } else {
          this.deselectAll();
        }
      },
    },

    /** @returns {boolean} */
    isDeleteRiverRumblesButtonDisabled() {
      return !this.hasSelectedRiverRumbles;
    },

    /** @returns {?string} */
    scheduleConflictMessage() {
      const intersectingIds = this.intersectingIds;
      if (intersectingIds === null) {
        return null;
      }
      if (intersectingIds.length === 0) {
        return null;
      }

      return 'Расписание конкурса кувшинок составлено неправильно. Расписание содержит пересекающиеся конкурсы кувшинок. Идентификаторы пересекающихся конкурсов кувшинок: ' + intersectingIds.join(', ') + '.';
    },

    /** @returns {boolean} */
    mayManageTimetable() {
      return isRoleAdmin(this.actor.role);
    },

    /** @returns {boolean} */
    isDeleteButtonVisible() {
      return isRoleAdmin(this.actor.role);
    },
  },

  created() {
    this.loadTimetable();
  },

  watch: {
    windowPastDuration() {
      this.loadTimetable();
    },

    windowFutureDuration() {
      this.loadTimetable();
    },
  },

  methods: {
    getPlayerGroupName,
    loadTimetable() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/river_rumble/timetable';

      const requestConf = {
        params: {}
      };

      const now = convertMomentToStamp(new Date());

      const windowPastDuration = this.windowPastDuration;
      if (windowPastDuration !== null) {
        requestConf.params.begin = getBegin(now, windowPastDuration);
      }

      const windowFutureDuration = this.windowFutureDuration;
      if (windowFutureDuration !== null) {
        requestConf.params.end = getEnd(now, windowFutureDuration);
      }

      HTTP.get(url, requestConf)
          .then(response => {
            const data = response.data.data;
            if (data === null) {
              notifier.showError('Invalid server response. data === null.', {sender: this});
              return;
            }

            if ('intersecting' in data) {
              this.intersectingIds = data.intersecting;
            }

            const riverRumbles = data.timetable || [];

            this.riverRumbleConts = [];
            for (const riverRumble of riverRumbles) {
              const riverRumbleCont = {
                selected: false,
                riverRumble: riverRumble,
              };
              this.riverRumbleConts.push(riverRumbleCont);
            }

            this.loadState = LOAD_STATE_LOADED_NORMAL;
          })
          .catch(err => {
            this.loadState = LOAD_STATE_LOADED_ERROR;
            notifier.showError(err, {sender: this});
          });
    },

    statusClass(record) {
      const status = RiverRumble.getStatus(record);
      return {
        'status-active': RiverRumble.isActive(status),
      };
    },

    getRiverRumbleClass(record) {
      const status = RiverRumble.getStatus(record);
      return {
        finished: RiverRumble.isFinished(status),
      };
    },

    getRiverRumbleProtoName(protoId) {
      return getProtoAlias(protoId);
    },

    isEditButtonVisible(riverRumble) {
      if (!isRoleAdmin(this.actor.role)) {
        return false;
      }
      return isRiverRumbleEditable(riverRumble);
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectAll() {
      for (const container of this.riverRumbleConts) {
        if (!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectAll() {
      for (const container of this.riverRumbleConts) {
        if (container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    deleteRecordWithConfirmation(riverRumble) {
      notifier.deleteSenderNotifications(this);

      this.confirmRiverRumbleDeletion(
          riverRumble.id,
          () => {
            this.deleteRecord(riverRumble);
          }
      );
    },

    confirmRiverRumbleDeletion(id, onConfirmed) {
      confirmAction(
          this,
          getRiverRumbleDeletionConfirmationMessage(id),
          'Delete',
          onConfirmed,
      );
    },

    deleteRecord(record) {
      const url = `/river_rumble/timetable/${record.id}/delete`;
      HTTP.post(url)
          .then(response => {
            const data = response.data.data;
            if (data === null) {
              notifier.showError('Invalid server response. data === null.', {sender: this});
              return;
            }

            deleteRiverRumbleContFromArray(this.riverRumbleConts, record.id);

            notifier.showSuccess('River rumble deleted.');
          })
          .catch(err => {
            notifier.showError(err, {sender: this});
          });
    },

    //-------------------------------------------------------------------------

    deleteSelectedRiverRumblesWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const ids = this.selectedRiverRumbleIds;

      this.confirmRiverRumblesDeletion(
          ids,
          () => {
            this.deleteRiverRumbles(ids);
          },
      );
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRiverRumblesDeletion(ids, onConfirmed) {
      const params = {};
      if (ids.length > 1) {
        params.description = getRiverRumbleIdsInfo(ids);
      }

      confirmAction(
          this,
          getRiverRumblesDeletionConfirmationMessage(ids),
          'Delete',
          onConfirmed,
          params,
      );
    },

    /** @param {number[]} ids */
    deleteRiverRumbles(ids) {
      const idsStr = ids.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/river_rumble/delete', data)
          .then(() => {
            deleteRiverRumbleContsFromArray(this.riverRumbleConts, ids);

            const message = getDeletionCompleteMessage(ids.length);
            notifier.showSuccess(message);
          })
          .catch(err => {
            notifier.showError(err, {sender: this});
          });
    },
  },

  filters: {
    getPlayerGroupName,

    shortRiverRumbleName: getShortRiverRumbleName,

    convertVersionCodeToStr,
    endAtStr: function (r) {
      return getMomentMinStr(r.stime + r.duration);
    },
    availableUntilStr: function (r) {
      return getMomentMinStr(r.stime + r.duration - r.min_time_until_end_for_start);
    },
    statusName: function (r) {
      const status = RiverRumble.getStatus(r);
      return RiverRumble.getStatusName(status);
    },
    boolToStr: function (v) {
      return v ? 'yes' : 'no';
    },

    segmentName(forQA) {
      return forQA ? 'QA' : 'all';
    },
  },
};

function getShortRiverRumbleName(protoId) {
  const alias = getProtoAlias(protoId);
  const PREFIX = '@river_rumble/';
  if (alias.startsWith(PREFIX)) {
    return alias.substring(PREFIX.length);
  }
  return alias;
}

function deleteRiverRumbleContsFromArray(conts, ids) {
  for (const id of ids) {
    deleteRiverRumbleContFromArray(conts, id);
  }
}

function deleteRiverRumbleContFromArray(conts, id) {
  const index = findRiverRumbleContIndex(conts, id);
  if (index !== null) {
    conts.splice(index, 1);
  }
}

function findRiverRumbleContIndex(conts, id) {
  for (let i = 0; i < conts.length; i++) {
    const cont = conts[i];
    if (cont.riverRumble.id === id) {
      return i;
    }
  }

  return null;
}

function getRiverRumbleDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete river rumble ${id} {env}?`
}

function getRiverRumblesDeletionConfirmationMessage(ids) {
  if (ids.length === 1) {
    const id = ids[0];
    return getRiverRumbleDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} river rumbles {env}?`;
}

function getRiverRumbleIdsInfo(ids) {
  return 'River rumble ids: ' + ids.join(', ') + '.';
}

function getDeletionCompleteMessage(amount) {
  if (amount === 1) {
    return 'River rumble deleted.'
  } else {
    return 'River rumbles deleted.'
  }
}
</script>

<style scoped>
.finished {
  color: #dcdcdc;
}

.status-active {
  color: var(--green);
}

.action-box {
  padding: 7px;
}

.action-box > .btn:last-child {
  margin-right: 0 !important;
}
</style>
